
import { useRef } from 'react';
import { useTranslations } from 'use-intl';
import { Route, Routes } from 'react-router-dom';
import { Sentry } from '@/lib/sentry';
import Index from './pages/Index';
import Auth from './pages/Auth';
import AuthConfirm from './pages/AuthConfirm';
import JoinCompany from './pages/JoinCompany';
import AcceptInvite from './pages/AcceptInvite';
import BankCallback from './pages/BankCallback'
import TaxCallback from './pages/TaxCallback'
import UserOnboarding from './pages/UserOnboarding';
import OnboardingSteps from './pages/OnboardingSteps';
import Overview from './pages/Overview';
import AccountPayables from './pages/AccountPayables';
import AccountPayablesApprovals from './pages/AccountPayablesApprovals';
import AccountReceivables from './pages/AccountReceivables';
import CreateInvoice from './pages/CreateInvoice';
import Invoices from './pages/Invoices';
import SalesReports from './pages/SalesReports';
import Payments from './pages/Payments';
import CashManagement from './pages/CashManagement';
import Bank from './pages/Bank';
import BankAccountDetails from './pages/BankAccountDetails';
import TaxAccountDetails from './pages/TaxAccountDetails';
import Transactions from './pages/Transactions';
import InvoiceUpload from './pages/InvoiceUpload';
import Upload from './pages/Upload';
import Profile from './pages/Profile';
import AccountBilling from './pages/AccountBilling';
import Settings from './pages/Settings';
import UserPermissions from './pages/UserPermissions';
import UserManagement from './pages/UserManagement';
import Review from './pages/Review';
import UnmatchedEmails from './pages/UnmatchedEmails';
import Expenses from './pages/Expenses';
import Receipts from './pages/Receipts';
import Messages from './pages/Messages';
import NotFound from './pages/NotFound';
import { JournalPage } from './pages/bookkeeping/JournalPage';
import Todos from './pages/Todos';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { OnboardingGuard } from './components/auth/OnboardingGuard';
import { PlatformAdminGuard } from './components/auth/PlatformAdminGuard';
import PlatformAdminViewGuard from './components/auth/PlatformAdminViewGuard';
import MainLayout from './components/layout/MainLayout';
import { PageEventsTracker } from './components/layout/PageEventsTracker';
import OnboardingLayout from './components/layout/OnboardingLayout';
import { isApprovalFlowEnabled, isInvoiceUploadEnabled, isSettingsEnabled, isUserPermissionsEnabled, isErpConnectionEnabled } from './config/features';
import { ErpCallbackHandler } from './components/erp/ErpCallbackHandler';
import { GmailCallbackHandler } from './components/messages/GmailCallbackHandler';

// Exported for a direct render test (App.test.tsx) — the full App tree pulls in every route's
// guards/pages, which is disproportionate to exercise just for this fallback's own rendering.
export function ErrorFallback({ reference }: { reference?: string }) {
  const t = useTranslations('consultantOnboarding.app.errorFallback');
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center p-8">
        <h1 className="text-2xl font-bold mb-4">{t('title')}</h1>
        <p className="text-muted-foreground mb-4">{t('body')}</p>
        {reference && (
          <p className="text-xs text-muted-foreground mb-4">{t('reference', { reference })}</p>
        )}
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
        >
          {t('refresh')}
        </button>
      </div>
    </div>
  );
}

function App() {
  // FIN-865 (review round 3, item 2) — `getLastCorrelationId()` names whatever the LAST
  // correlated fetch happened to send, which need not be the request the render crash actually
  // belongs to (or any request at all, for a pure client-side throw). Minting a fresh id here
  // and tagging the crash event with it (beforeCapture, before Sentry.captureException fires)
  // instead makes the reference deterministically resolve to THIS Sentry event — a ref persists
  // it from beforeCapture's callback to the fallback's own render for the same crash.
  const crashReferenceRef = useRef<string | undefined>(undefined);

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        const correlationId = crypto.randomUUID();
        crashReferenceRef.current = correlationId.slice(0, 8);
        scope.setTag('correlation_id', correlationId);
      }}
      fallback={() => <ErrorFallback reference={crashReferenceRef.current} />}
    >
    <PageEventsTracker />
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/confirm" element={<AuthConfirm />} />

      {/* Invitation route - accessible without full authentication */}
      <Route path="/accept-invite" element={<AcceptInvite />} />
      {/* Public: the SCA popup Enable Banking redirects carries no session of its own, and
          the connection completes in the original tab regardless (FIN-709). */}
      <Route path="/bank/callback" element={<BankCallback />} />
      {/* Public for the same reason: Skatteverket's popup, sent here by skatteverket-capture-redirect
          (FIN-980). */}
      <Route path="/tax/callback" element={<TaxCallback />} />

      {/* Onboarding routes - only ProtectedRoute (no onboarding guard) */}
      <Route element={<ProtectedRoute />}>
        <Route element={<OnboardingLayout />}>
          <Route path="/join-company" element={<JoinCompany />} />
          <Route path="/user-onboarding" element={<UserOnboarding />} />
          <Route path="/onboarding-steps" element={<OnboardingSteps />} />
        </Route>
      </Route>
      
      {/* Main app routes - with ProtectedRoute, PlatformAdminGuard and OnboardingGuard */}
      <Route element={<ProtectedRoute />}>
        <Route element={<PlatformAdminGuard />}>
        <Route element={<PlatformAdminViewGuard />}>
        <Route element={<OnboardingGuard />}>
          {isErpConnectionEnabled() && (
            <Route path="/erp/callback" element={<ErpCallbackHandler />} />
          )}
          <Route path="/gmail/callback" element={<GmailCallbackHandler />} />
          <Route element={<MainLayout />}>
            <Route path="/overview" element={<Overview />} />
            <Route path="/account-payables" element={<AccountPayables />} />
            <Route path="/account-payables/payments" element={<AccountPayables />} />
            {isApprovalFlowEnabled() && (
              <>
                <Route path="/account-payables/approvals" element={<AccountPayablesApprovals />} />
                <Route path="/account-payables-approvals" element={<AccountPayablesApprovals />} />
              </>
            )}
            <Route path="/account-receivables" element={<AccountReceivables />} />
            <Route path="/create-invoice" element={<CreateInvoice />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/sales-reports" element={<SalesReports />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/cash-management" element={<CashManagement />} />
            <Route path="/bank" element={<Bank />} />
            <Route path="/bank-account/:id" element={<BankAccountDetails />} />
            <Route path="/tax-account/:id" element={<TaxAccountDetails />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/upload" element={<Upload />} />
            {isInvoiceUploadEnabled() && (
              <Route path="/invoice-upload" element={<InvoiceUpload />} />
            )}
            <Route path="/profile" element={<Profile />} />
            <Route path="/account-billing" element={<AccountBilling />} />
            {isSettingsEnabled() && (
              <Route path="/settings" element={<Settings />} />
            )}
            {isUserPermissionsEnabled() && (
              <Route path="/user-permissions" element={<UserPermissions />} />
            )}
            <Route path="/user-management" element={<UserManagement />} />
            <Route path="/review" element={<Review />} />
            <Route path="/todos" element={<Todos />} />
            <Route path="/unmatched-emails" element={<UnmatchedEmails />} />
            <Route path="/expenses" element={<Expenses />} />
            <Route path="/receipts" element={<Receipts />} />
            <Route path="/messages/:threadId?" element={<Messages />} />
            <Route path="/bookkeeping/journal" element={<JournalPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
        </Route>
        </Route>
      </Route>
    </Routes>
    </Sentry.ErrorBoundary>
  );
}

export default App;
